import React, { useState, useEffect, useRef } from 'react';
import { customFetch } from '../apiClient'; 
import Swal from 'sweetalert2';
import { API_BASE } from '../config'; 

const USGReportHeaderConfig = () => {
    const [initialLoading, setInitialLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Form navigation ke liye ref
    const formRef = useRef(null);
    
    const [formData, setFormData] = useState({
        headerId: 0,
        hid: 0,
        hospitalName: '',
        subName: '',
        slogan: '',
        regNo: '',
        email: '',
        address: '',
        landmark: '',
        state: '',
        district: '',
        pincode: '',
        contact1: '',
        contact2: '',
        websiteURL: '',
        isAyushmanActive: false
    });

    const [logoFile, setLogoFile] = useState(null);
    const [ayushmanLogoFile, setAyushmanLogoFile] = useState(null);
    const [previews, setPreviews] = useState({ logo: '', ayushman: '' });

    // ✅ IMAGE URL GENERATOR
    const getImageUrl = (path) => {
        if (!path) return '';
        const base = API_BASE.replace(/\/api\/?$/, ''); 
        const cleanPath = path.replace(/^\//, ''); 
        return `${base}/${cleanPath}`;
    };

    // 1. Load Data on Mount
    useEffect(() => {
        const fetchHeaderData = async () => {
            try {
                const response = await customFetch('/USGReportHeader');
                if (response.ok) {
                    const data = await response.json();
                    console.log("💥 BACKEND SE YE DATA AAYA HAI:", data);
                    // ✅ FIXED: Case insensitivity handle ki gayi hai (.NET PascalCase vs camelCase issue)
                    setFormData({
                        headerId: data.headerId || data.HeaderId || 0,
                        hid: data.hid || data.HID || 0,
                        hospitalName: data.hospitalName || data.HospitalName || '',
                        subName: data.subName || data.SubName || '',
                        slogan: data.slogan || data.Slogan || '',
                        regNo: data.regNo || data.RegNo || '',
                        email: data.email || data.Email || '',
                        address: data.address || data.Address || '',
                        landmark: data.landmark || data.Landmark || '',
                        state: data.state || data.State || '',
                        district: data.district || data.District || '',
                        pincode: data.pincode || data.Pincode || '',
                        contact1: data.contact1 || data.Contact1 || '',
                        contact2: data.contact2 || data.Contact2 || '',
                        websiteURL: data.websiteURL || data.WebsiteURL || '',
                        isAyushmanActive: data.isAyushmanActive ?? data.IsAyushmanActive ?? false
                    });

                    // Set Image Previews
                    const mainLogo = data.logo || data.Logo;
                    const ayushLogo = data.ayushmanLogo || data.AyushmanLogo;
                    
                    setPreviews({
                        logo: mainLogo ? getImageUrl(mainLogo) : '',
                        ayushman: ayushLogo ? getImageUrl(ayushLogo) : ''
                    });
                }
            } catch (error) {
                console.error("Failed to load header data", error);
                Swal.fire('Error', 'Could not load configuration. Please try again.', 'error');
            } finally {
                setInitialLoading(false);
            }
        };

        fetchHeaderData();
    }, []);

    // 2. Keyboard Navigation (Enter key to move next)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'Enter') return;
            if (e.target.closest('.react-select__menu')) return; // Ignore if using react-select dropdowns

            const focusableSelector = 
                'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
                'select:not([disabled]), ' +
                'textarea:not([disabled]):not([readonly]), ' +
                'button:not([disabled])';

            const focusableElements = Array.from(
                formRef.current?.querySelectorAll(focusableSelector) || []
            );

            if (focusableElements.length === 0) return;
            const currentIndex = focusableElements.indexOf(document.activeElement);

            if (currentIndex === focusableElements.length - 1) {
                e.preventDefault();
                formRef.current?.requestSubmit(); // Last element pe aake Enter dabane pe submit hoga
                return;
            }

            if (currentIndex >= 0) {
                e.preventDefault();
                focusableElements[currentIndex + 1]?.focus(); // Next element pe focus
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleTextChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Optional: Ensure only numbers are typed in contact fields
        if ((name === 'contact1' || name === 'contact2') && value.match(/[^0-9]/)) {
            return;
        }

        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                Swal.fire('Warning', 'Image size should be less than 2MB', 'warning');
                return;
            }

            if (type === 'main') {
                setLogoFile(file);
                setPreviews(p => ({ ...p, logo: URL.createObjectURL(file) }));
            } else {
                setAyushmanLogoFile(file);
                setPreviews(p => ({ ...p, ayushman: URL.createObjectURL(file) }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const fd = new FormData();
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null && formData[key] !== undefined) {
                fd.append(key, formData[key]);
            }
        });

        if (logoFile) fd.append('LogoFile', logoFile);
        if (ayushmanLogoFile) fd.append('AyushmanLogoFile', ayushmanLogoFile);

        try {
            const response = await customFetch('/USGReportHeader', {
                method: 'POST',
                body: fd 
            });

            if (response.ok) {
                const result = await response.json(); 
                
                Swal.fire({
                    title: 'Saved!',
                    text: 'USG Report Header configuration saved successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                if (result.logoUrl) setPreviews(p => ({ ...p, logo: getImageUrl(result.logoUrl) }));
                if (result.ayushmanLogoUrl) setPreviews(p => ({ ...p, ayushman: getImageUrl(result.ayushmanLogoUrl) }));

                setLogoFile(null);
                setAyushmanLogoFile(null);

            } else {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to save configuration.');
            }
        } catch (error) {
            console.error(error);
            Swal.fire('Error', error.message || 'Something went wrong while saving.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleClearAll = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This will clear all text fields on the screen.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, clear it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => ({
                    ...prev,
                    hospitalName: '', subName: '', slogan: '', regNo: '', email: '',
                    address: '', landmark: '', state: '', district: '', pincode: '',
                    contact1: '', contact2: '', websiteURL: '', isAyushmanActive: false
                }));
            }
        });
    };

    if (initialLoading) {
        return (
            <div className="profile-page-container d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                <span className="ms-3 text-muted fw-bold">Loading Header Data...</span>
            </div>
        );
    }

    return (
        <div className="profile-page-container">
            <div className="page-header">
                <h2><i className="bi bi-file-earmark-medical me-2"></i> USG Report Header Config</h2>
                <p>Manage the print header details and logos specifically for Ultrasound (USG) Reports.</p>
            </div>

            {/* ✅ Added ref={formRef} here for Keyboard Navigation */}
            <form onSubmit={handleSubmit} className="profile-form" ref={formRef}>
                
                {/* --- Section 1: Branding & Logos --- */}
                <div className="profile-card branding-section">
                    <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                        <h5 className="section-title border-0 pb-0 mb-0"><i className="bi bi-image text-warning"></i> Logos for Printing</h5>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleClearAll} disabled={saving}>
                           <i className="bi bi-eraser-fill me-1"></i> Clear Fields
                        </button>
                    </div>
                    
                    <div className="logo-upload-grid">
                        <div className="logo-box">
                            <label>USG Main Logo</label>
                            <div className="preview-circle">
                                {previews.logo ? <img src={previews.logo} alt="Logo" /> : <i className="bi bi-image"></i>}
                                <div className="overlay-hover">
                                    <label htmlFor="mainLogo"><i className="bi bi-camera-fill"></i> Change</label>
                                </div>
                                <input type="file" onChange={(e) => handleFileChange(e, 'main')} accept="image/*" id="mainLogo" hidden />
                            </div>
                        </div>
                        <div className="logo-box">
                            <label>USG Ayushman Logo</label>
                            <div className="preview-circle ayushman">
                                {previews.ayushman ? <img src={previews.ayushman} alt="Ayushman" /> : <i className="bi bi-shield-plus"></i>}
                                <div className="overlay-hover">
                                    <label htmlFor="ayushLogo"><i className="bi bi-camera-fill"></i> Change</label>
                                </div>
                                <input type="file" onChange={(e) => handleFileChange(e, 'ayushman')} accept="image/*" id="ayushLogo" hidden />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Section 2: Basic Info --- */}
                <div className="profile-card">
                    <h5 className="section-title"><i className="bi bi-hospital text-primary"></i> Basic Details</h5>
                    <div className="form-grid">
                        <div className="input-group">
                            <label>Hospital Name <span className="text-danger">*</span></label>
                            <input name="hospitalName" value={formData.hospitalName} onChange={handleTextChange} required className="hp-input" placeholder="Enter Header Title" />
                        </div>
                        <div className="input-group">
                            <label>Sub Name / Clinic Name</label>
                            <input name="subName" value={formData.subName} onChange={handleTextChange} className="hp-input" placeholder="e.g. And Ultrasound Clinic" />
                        </div>
                        <div className="input-group">
                            <label>Registration Number</label>
                            <input name="regNo" value={formData.regNo} onChange={handleTextChange} className="hp-input" placeholder="e.g. REG-12345" />
                        </div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group full-width">
                            <label>Slogan / Tagline</label>
                            <input name="slogan" value={formData.slogan} onChange={handleTextChange} className="hp-input" placeholder="Enter catchy slogan or tagline" />
                        </div>
                    </div>
                </div>

                {/* --- Section 3: Contact & Address --- */}
                <div className="profile-card">
                    <h5 className="section-title"><i className="bi bi-geo-alt text-success"></i> Location & Contact</h5>
                    <div className="form-grid">
                        <div className="input-group">
                            <label>Email Address</label>
                            <input name="email" type="email" value={formData.email} onChange={handleTextChange} className="hp-input" placeholder="hospital@example.com" />
                        </div>
                        <div className="input-group">
                            <label>Contact Number 1</label>
                            {/* ✅ maxLength 10 added */}
                            <input name="contact1" value={formData.contact1} onChange={handleTextChange} className="hp-input" placeholder="10 Digit Number" maxLength={10} />
                        </div>
                        <div className="input-group">
                            <label>Contact Number 2</label>
                            {/* ✅ maxLength 10 added */}
                            <input name="contact2" value={formData.contact2} onChange={handleTextChange} className="hp-input" placeholder="10 Digit Number" maxLength={10} />
                        </div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group full-width">
                            <label>Full Address</label>
                            <textarea name="address" value={formData.address} onChange={handleTextChange} className="hp-input" rows="2" placeholder="Street, Locality, Area" />
                        </div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group"><label>Landmark</label><input name="landmark" value={formData.landmark} onChange={handleTextChange} className="hp-input" placeholder="Near by place" /></div>
                        <div className="input-group"><label>District</label><input name="district" value={formData.district} onChange={handleTextChange} className="hp-input" placeholder="District" /></div>
                        <div className="input-group"><label>State</label><input name="state" value={formData.state} onChange={handleTextChange} className="hp-input" placeholder="State" /></div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group"><label>Pincode</label><input name="pincode" value={formData.pincode} onChange={handleTextChange} className="hp-input" placeholder="PIN Code" /></div>
                        <div className="input-group" style={{ gridColumn: 'span 2' }}>
                            <label>Website URL</label>
                            <input name="websiteURL" value={formData.websiteURL} onChange={handleTextChange} className="hp-input" placeholder="www.hospital.com" />
                        </div>
                    </div>
                </div>

                {/* --- Section 4: System Features --- */}
                <div className="profile-card">
                    <h5 className="section-title"><i className="bi bi-printer text-secondary"></i> Print Preferences</h5>
                    <div className="feature-row">
                        <div className="feature-details">
                            <h6>Print Ayushman Logo</h6>
                            <p>Enable this to automatically append the Ayushman Bharat logo on USG printouts.</p>
                        </div>
                        <div className="feature-toggle">
                            <span className={formData.isAyushmanActive ? "text-success fw-bold me-3" : "text-muted me-3"}>
                                {formData.isAyushmanActive ? "Active" : "Hidden"}
                            </span>
                            <label className="custom-switch">
                                <input type="checkbox" name="isAyushmanActive" checked={formData.isAyushmanActive} onChange={handleTextChange} />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="sticky-footer">
                    <button type="submit" className="save-btn" disabled={saving}>
                        {saving ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Saving...</>
                        ) : (
                            <><i className="bi bi-cloud-arrow-up-fill me-2"></i> Save USG Configuration</>
                        )}
                    </button>
                </div>
            </form>

            <style jsx>{`
                .profile-page-container { max-width: 1000px; margin: 0 auto; padding: 20px 20px 100px 20px; }
                
                .page-header { margin-bottom: 25px; }
                .page-header h2 { font-size: 24px; font-weight: 700; color: #1e293b; margin-bottom: 5px; }
                .page-header p { color: #64748b; font-size: 14px; margin: 0; }

                .profile-card { background: white; border-radius: 12px; padding: 25px; margin-bottom: 25px; box-shadow: 0 2px 12px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; }
                .section-title { font-size: 15px; font-weight: 700; color: #334155; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px; }
                
                /* Image Upload UX */
                .logo-upload-grid { display: flex; gap: 50px; justify-content: center; padding: 10px 0; }
                .logo-box { text-align: center; }
                .logo-box label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 12px; color: #475569; }
                
                .preview-circle { width: 120px; height: 120px; border-radius: 50%; background: #f8fafc; border: 2px dashed #cbd5e1; display: flex; align-items: center; justify-content: center; position: relative; font-size: 40px; color: #94a3b8; overflow: hidden; transition: 0.3s; }
                .preview-circle img { width: 100%; height: 100%; object-fit: cover; }
                
                .overlay-hover { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; opacity: 0; transition: 0.3s; }
                .preview-circle:hover .overlay-hover { opacity: 1; }
                .overlay-hover label { color: white; cursor: pointer; font-size: 12px; font-weight: 600; margin: 0; display: flex; flex-direction: column; align-items: center; gap: 5px; }
                .overlay-hover label i { font-size: 20px; }

                /* Forms */
                .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
                .full-width { grid-column: span 3; }
                .input-group { display: flex; flex-direction: column; gap: 6px; }
                .input-group label { font-size: 12px; font-weight: 600; color: #475569; }
                
                .hp-input { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; color: #1e293b; background: #f8fafc; transition: all 0.3s ease; }
                .hp-input:focus { background: white; border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 3px rgba(0, 136, 204, 0.15); outline: none; }
                .hp-input::placeholder { color: #94a3b8; }

                /* Feature Toggle Row */
                .feature-row { display: flex; justify-content: space-between; align-items: center; padding: 15px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
                .feature-details h6 { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 4px 0; }
                .feature-details p { font-size: 12px; color: #64748b; margin: 0; }
                .feature-toggle { display: flex; align-items: center; }

                /* Custom CSS Switch */
                .custom-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                .custom-switch input { opacity: 0; width: 0; height: 0; }
                .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; }
                .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
                input:checked + .slider { background-color: #10b981; }
                input:focus + .slider { box-shadow: 0 0 1px #10b981; }
                input:checked + .slider:before { transform: translateX(20px); }
                .slider.round { border-radius: 24px; }
                .slider.round:before { border-radius: 50%; }

                /* Footer */
                .sticky-footer { position: fixed; bottom: 0; left: 0; right: 0; background: rgba(255,255,255,0.9); backdrop-filter: blur(10px); padding: 15px; border-top: 1px solid #e2e8f0; display: flex; justify-content: center; z-index: 100; box-shadow: 0 -4px 20px rgba(0,0,0,0.05); }
                .save-btn { background: var(--theme-color, #0088cc); color: white; border: none; padding: 12px 40px; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; display: flex; align-items: center; transition: 0.3s; box-shadow: 0 4px 12px rgba(0, 136, 204, 0.3); }
                .save-btn:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.1); }
                
                @media (max-width: 768px) { 
                    .form-grid { grid-template-columns: 1fr; gap: 15px; } 
                    .full-width { grid-column: span 1; } 
                    .logo-upload-grid { flex-direction: column; gap: 20px; align-items: center; }
                    .feature-row { flex-direction: column; align-items: flex-start; gap: 15px; }
                }
            `}</style>
        </div>
    );
};

export default USGReportHeaderConfig;